import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { ITimer } from '../interfaces/ITimer';
import { SessionStorageService } from './session-storage.service';
import { LogService } from './log.service';
import { AwsApiService } from './aws-api.service';
import { environment } from '../../environments/environment';
import { AuditService } from './audit.service';
import { domRendererFactory3 } from '../../../node_modules/@angular/core/src/render3/interfaces/renderer';

@Injectable({
  providedIn: 'root'
})
export class TokenTimerService implements ITimer {
  private timer: number;
  private onSessionTerminate: Function;

  constructor(
    private sessionStorage: SessionStorageService,
    private log: LogService,
    private api: AwsApiService
  ) {}

  onSessionTerminateEvent(onSessionTerminate: Function) {
    this.onSessionTerminate = onSessionTerminate;
  }

  start() {
    if (!this.timer) {
      const intervalInMillis = environment.settings.token_timer.interval;

      this.checkForRefreshToken();

      this.timer = window.setInterval(() => {
        this.checkForRefreshToken();
      }, intervalInMillis);
    }
  }

  stop() {
    if (this.timer) {
      window.clearTimeout(this.timer);
    }
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }

  reset() {}

  private checkForRefreshToken() {
    const token = this.sessionStorage.getIdToken();
    const millisTillExpiration = token.getMillisTillExpiration();
    const settings = environment.settings.token_timer;

    this.log.trace('TokenTimer.checkForRefreshToken', {
      millisTillExpiration: millisTillExpiration,
      max_trigger: settings.max_trigger,
      min_trigger: settings.min_trigger,
      millisTillRefresh: millisTillExpiration - settings.max_trigger
    });

    if (millisTillExpiration > settings.max_trigger) {
      // do nothing, token is still valid
    } else if (
      millisTillExpiration <= settings.max_trigger &&
      millisTillExpiration >= settings.min_trigger
    ) {
      // token expiration is within refresh window, proceed with refresh
      this.refreshToken();
    } else if (millisTillExpiration < settings.min_trigger) {
      // shouldn't get here, but if it does, not enough time to refresh token
      // terminate session
      this.onSessionTerminate();
    }
  }

  private refreshToken() {
    // stop timer before the api call
    this.stop();

    const params = {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: this.sessionStorage.getClientId(),
      AuthParameters: {
        REFRESH_TOKEN: this.sessionStorage.getRefreshToken(),
        // Try to include a device key for this session, if it was not remembered
        DEVICE_KEY: this.sessionStorage.getTemporaryDeviceKey()
      }
    };

    // Otherwise include the remembered device key
    if (!params.AuthParameters.DEVICE_KEY) {
      const username = this.sessionStorage.getUser().username;
      params.AuthParameters.DEVICE_KEY = this.sessionStorage.getDeviceKey(
        username
      );
    }

    const doRefresh = () => {
      this.api.cognito().initiateAuth(params, (err, data) => {
        if (err) {
          this.log.error(err);

          // The request may have been aborted due to the user navigating while it was active.
          // In this case, do not terminate session.
          if (_.get(err, 'code') === 'RequestAbortedError') {
            this.log.trace('Refresh aborted, delaying');
          }
          // If this failed due to an "Invalid refresh token" NotAuthorizedException, try again with no device key
          else if (
            _.get(err, 'code', 'NotAuthorizedException') &&
            params.AuthParameters.DEVICE_KEY
          ) {
            delete params.AuthParameters.DEVICE_KEY;
            doRefresh();
          }
          // Otherwise this is a real failure
          else {
            // something bad happened, terminate session
            this.log.trace('Refresh error, terminating session');
            this.onSessionTerminate();
          }
        } else {
          this.log.trace('refresh token response', data);

          // set new token
          this.sessionStorage.setRefreshTokenAuthResult(
            data.AuthenticationResult
          );

          // start up the timer again
          this.start();
        }
      });
    };

    doRefresh();
  }
}
